import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Plus, Edit2, Trash2, Eye, EyeOff, 
  FolderOpen, FileText, HelpCircle, Search, CheckCircle
} from 'lucide-react';
import PageHeader from '../components/PageHeader';
import * as educationService from '../services/educationService';
import type { EducationCategory, EducationArticle, EducationFAQ } from '../services/educationService';

type TabType = 'categories' | 'articles' | 'faqs';

export default function AdminEducationDashboard() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<TabType>('categories');
  
  // Categories
  const [categories, setCategories] = useState<EducationCategory[]>([]);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState<EducationCategory | null>(null);
  const [categoryForm, setCategoryForm] = useState({
    name: '',
    slug: '',
    description: '',
    icon: '',
    order: 0,
    isActive: true
  });
  const [savingCategory, setSavingCategory] = useState(false);
  
  // Articles
  const [articles, setArticles] = useState<EducationArticle[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  
  // FAQs
  const [faqs, setFaqs] = useState<EducationFAQ[]>([]);
  const [showFaqModal, setShowFaqModal] = useState(false);
  const [editingFaq, setEditingFaq] = useState<EducationFAQ | null>(null);
  const [faqForm, setFaqForm] = useState({
    question: '',
    answer: '',
    categoryId: '',
    tags: [] as string[],
    order: 0,
    isPublished: true
  });
  const [savingFaq, setSavingFaq] = useState(false);

  useEffect(() => {
    loadData();
  }, [activeTab]);

  const loadData = async () => {
    try {
      if (activeTab === 'categories') {
        const data = await educationService.getAdminCategories();
        setCategories(data);
      } else if (activeTab === 'articles') {
        const data = await educationService.getAdminArticles();
        setArticles(data);
      } else if (activeTab === 'faqs') {
        const data = await educationService.getAdminFAQs();
        setFaqs(data);
      }
    } catch (error) {
      console.error('Error loading data:', error);
    }
  };

  // Auto-generate slug from name
  const generateSlug = (name: string) => {
    return name
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '') // Remove diacritics
      .replace(/[^a-z0-9\s-]/g, '') // Remove special chars
      .trim()
      .replace(/\s+/g, '-') // Replace spaces with -
      .replace(/-+/g, '-'); // Replace multiple - with single -
  };

  const openCategoryModal = (category: EducationCategory | null) => {
    if (category) {
      // Edit mode
      setEditingCategory(category);
      setCategoryForm({
        name: category.name,
        slug: category.slug,
        description: category.description || '',
        icon: category.icon || '',
        order: category.order,
        isActive: category.isActive
      });
    } else {
      // Create mode
      setEditingCategory(null);
      setCategoryForm({
        name: '',
        slug: '',
        description: '',
        icon: '',
        order: categories.length,
        isActive: true
      });
    }
    setShowCategoryModal(true);
  };

  const handleCategoryFormChange = (field: string, value: any) => {
    setCategoryForm(prev => {
      const updated = { ...prev, [field]: value };
      
      // Auto-generate slug when name changes (only if slug is empty or in create mode)
      if (field === 'name' && (!editingCategory || !prev.slug)) {
        updated.slug = generateSlug(value);
      }
      
      return updated;
    });
  };

  const handleSaveCategory = async () => {
    // Validation
    if (!categoryForm.name.trim()) {
      alert('Numele categoriei este obligatoriu!');
      return;
    }
    
    if (!categoryForm.slug.trim()) {
      alert('Slug-ul este obligatoriu!');
      return;
    }

    setSavingCategory(true);
    try {
      if (editingCategory) {
        // Update
        await educationService.updateCategory(editingCategory.id, categoryForm);
      } else {
        // Create
        await educationService.createCategory(categoryForm);
      }
      
      setShowCategoryModal(false);
      loadData();
      alert(editingCategory ? '✅ Categorie actualizată!' : '✅ Categorie creată!');
    } catch (error: any) {
      alert(error.response?.data?.error || 'Eroare la salvare');
    } finally {
      setSavingCategory(false);
    }
  };

  const handleDeleteCategory = async (id: string) => {
    if (!confirm('Sigur vrei să ștergi această categorie?')) return;
    try {
      await educationService.deleteCategory(id);
      loadData();
      alert('✅ Categorie ștearsă!');
    } catch (error: any) {
      alert(error.response?.data?.error || 'Eroare la ștergere');
    }
  };

  const handleToggleArticlePublish = async (id: string) => {
    try {
      await educationService.toggleArticlePublish(id);
      loadData();
    } catch (error) {
      alert('Eroare la schimbarea stării');
    }
  };

  const handleDeleteArticle = async (id: string) => {
    if (!confirm('Sigur vrei să ștergi acest articol?')) return;
    try {
      await educationService.deleteArticle(id);
      loadData();
    } catch (error) {
      alert('Eroare la ștergere');
    }
  };

  const openFaqModal = (faq: EducationFAQ | null) => {
    if (faq) {
      // Edit mode
      setEditingFaq(faq);
      setFaqForm({
        question: faq.question,
        answer: faq.answer,
        categoryId: faq.categoryId || '',
        tags: faq.tags,
        order: faq.order,
        isPublished: faq.isPublished
      });
    } else {
      // Create mode
      setEditingFaq(null);
      setFaqForm({
        question: '',
        answer: '',
        categoryId: '',
        tags: [],
        order: faqs.length,
        isPublished: true
      });
    }
    setShowFaqModal(true);
  };

  const handleFaqFormChange = (field: string, value: any) => {
    setFaqForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSaveFaq = async () => {
    // Validation
    if (!faqForm.question.trim()) {
      alert('Întrebarea este obligatorie!');
      return;
    }
    
    if (!faqForm.answer.trim()) {
      alert('Răspunsul este obligatoriu!');
      return;
    }

    setSavingFaq(true);
    try {
      const data = {
        question: faqForm.question,
        answer: faqForm.answer,
        categoryId: faqForm.categoryId || undefined,
        tags: faqForm.tags,
        order: faqForm.order,
        isPublished: faqForm.isPublished
      };

      if (editingFaq) {
        // Update
        await educationService.updateFAQ(editingFaq.id, data);
      } else {
        // Create
        await educationService.createFAQ(data);
      }
      
      setShowFaqModal(false);
      loadData();
      alert(editingFaq ? '✅ FAQ actualizat!' : '✅ FAQ creat!');
    } catch (error: any) {
      alert(error.response?.data?.error || 'Eroare la salvare');
    } finally {
      setSavingFaq(false);
    }
  };

  const handleDeleteFaq = async (id: string) => {
    if (!confirm('Sigur vrei să ștergi acest FAQ?')) return;
    try {
      await educationService.deleteFAQ(id);
      loadData();
      alert('✅ FAQ șters!');
    } catch (error) {
      alert('Eroare la ștergere');
    }
  };

  const filteredArticles = articles.filter(article =>
    article.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    article.category?.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-blue-50">
      <PageHeader 
        title="Education Module"
        subtitle="Gestionează conținut educațional"
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Tabs */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 mb-6">
          <div className="flex border-b border-gray-200">
            <button
              onClick={() => setActiveTab('categories')}
              className={`flex items-center gap-2 px-6 py-4 font-medium transition-colors ${
                activeTab === 'categories'
                  ? 'text-purple-600 border-b-2 border-purple-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <FolderOpen className="w-5 h-5" />
              Categorii ({categories.length})
            </button>
            <button
              onClick={() => setActiveTab('articles')}
              className={`flex items-center gap-2 px-6 py-4 font-medium transition-colors ${
                activeTab === 'articles'
                  ? 'text-purple-600 border-b-2 border-purple-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <FileText className="w-5 h-5" />
              Articole ({articles.length})
            </button>
            <button
              onClick={() => setActiveTab('faqs')}
              className={`flex items-center gap-2 px-6 py-4 font-medium transition-colors ${
                activeTab === 'faqs'
                  ? 'text-purple-600 border-b-2 border-purple-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <HelpCircle className="w-5 h-5" />
              FAQs ({faqs.length})
            </button>
          </div>
        </div>

        {/* Categories Tab */}
        {activeTab === 'categories' && (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <h2 className="text-2xl font-bold text-gray-900">Categorii</h2>
              <button
                onClick={() => openCategoryModal(null)}
                className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
              >
                <Plus className="w-5 h-5" />
                Categorie Nouă
              </button>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {categories.map((category) => (
                <div key={category.id} className="bg-white rounded-xl p-6 shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      {category.icon && <span className="text-2xl">{category.icon}</span>}
                      <div>
                        <h3 className="font-semibold text-gray-900">{category.name}</h3>
                        <p className="text-sm text-gray-500">/{category.slug}</p>
                      </div>
                    </div>
                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                      category.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                    }`}>
                      {category.isActive ? 'Activă' : 'Inactivă'}
                    </span>
                  </div>

                  {category.description && (
                    <p className="text-sm text-gray-600 mb-4">{category.description}</p>
                  )}

                  <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                    <span className="text-sm text-gray-500">
                      {category._count?.articles || 0} articole
                    </span>
                    <div className="flex gap-2">
                      <button
                        onClick={() => openCategoryModal(category)}
                        className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteCategory(category.id)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Articles Tab */}
        {activeTab === 'articles' && (
          <div className="space-y-6">
            <div className="flex justify-between items-center gap-4">
              <div className="flex-1 max-w-md">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Caută articole..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>
              </div>
              <button
                onClick={() => navigate('/admin/education/article/new')}
                className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors whitespace-nowrap"
              >
                <Plus className="w-5 h-5" />
                Articol Nou
              </button>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Articol</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Categorie</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tip</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Vizualizări</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Acțiuni</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredArticles.map((article) => (
                    <tr key={article.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <div>
                          <div className="font-medium text-gray-900">{article.title}</div>
                          {article.excerpt && (
                            <div className="text-sm text-gray-500 line-clamp-1">{article.excerpt}</div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">{article.category?.name || '-'}</td>
                      <td className="px-6 py-4">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          article.type === 'VIDEO' ? 'bg-red-100 text-red-800' :
                          article.type === 'FAQ' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-blue-100 text-blue-800'
                        }`}>
                          {article.type}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          {article.isPublished ? (
                            <span className="flex items-center gap-1 text-green-600">
                              <CheckCircle className="w-4 h-4" />
                              <span className="text-sm">Publicat</span>
                            </span>
                          ) : (
                            <span className="text-sm text-gray-500">Draft</span>
                          )}
                          {article.isFeatured && (
                            <span className="px-2 py-1 bg-yellow-100 text-yellow-800 rounded text-xs font-medium">★ Featured</span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">{article.viewCount}</td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex justify-end gap-2">
                          <button
                            onClick={() => handleToggleArticlePublish(article.id)}
                            className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                            title={article.isPublished ? 'Ascunde' : 'Publică'}
                          >
                            {article.isPublished ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          </button>
                          <button
                            onClick={() => navigate(`/admin/education/article/${article.id}`)}
                            className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteArticle(article.id)}
                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* FAQs Tab */}
        {activeTab === 'faqs' && (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <h2 className="text-2xl font-bold text-gray-900">FAQs</h2>
              <button
                onClick={() => openFaqModal(null)}
                className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
              >
                <Plus className="w-5 h-5" />
                FAQ Nou
              </button>
            </div>

            <div className="space-y-4">
              {faqs.map((faq) => (
                <div key={faq.id} className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
                  <div className="flex items-start justify-between mb-3">
                    <h3 className="font-semibold text-gray-900 flex-1">{faq.question}</h3>
                    <div className="flex items-center gap-2 ml-4">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                        faq.isPublished ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                      }`}>
                        {faq.isPublished ? 'Publicat' : 'Draft'}
                      </span>
                      <button
                        onClick={() => openFaqModal(faq)}
                        className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteFaq(faq.id)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  <p className="text-gray-600 mb-3">{faq.answer}</p>
                  <div className="text-sm text-gray-500">
                    {faq.viewCount} vizualizări
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Category Modal - Complete Professional Version */}
      {showCategoryModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 overflow-y-auto">
          <div className="bg-white rounded-xl max-w-2xl w-full my-8">
            {/* Header */}
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-xl font-bold text-gray-900">
                {editingCategory ? '✏️ Editează Categorie' : '➕ Categorie Nouă'}
              </h3>
              <button
                onClick={() => setShowCategoryModal(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Form */}
            <div className="px-6 py-4 space-y-4 max-h-[calc(100vh-200px)] overflow-y-auto">
              {/* Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nume Categorie <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={categoryForm.name}
                  onChange={(e) => handleCategoryFormChange('name', e.target.value)}
                  placeholder="ex: Nutriție și Dietă"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  autoFocus
                />
              </div>

              {/* Slug */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Slug (URL) <span className="text-red-500">*</span>
                </label>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-500">/educatie/</span>
                  <input
                    type="text"
                    value={categoryForm.slug}
                    onChange={(e) => handleCategoryFormChange('slug', e.target.value)}
                    placeholder="nutritie-si-dieta"
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Se generează automat din nume. Folosește doar litere mici, cifre și cratime.
                </p>
              </div>

              {/* Icon */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Icon (Emoji)
                </label>
                <input
                  type="text"
                  value={categoryForm.icon}
                  onChange={(e) => handleCategoryFormChange('icon', e.target.value)}
                  placeholder="🍎 📚 💪 🧘"
                  maxLength={2}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-2xl"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Adaugă un emoji pentru a face categoria mai atractivă
                </p>
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Descriere
                </label>
                <textarea
                  value={categoryForm.description}
                  onChange={(e) => handleCategoryFormChange('description', e.target.value)}
                  placeholder="Articole despre nutriție sănătoasă, diete echilibrate și sfaturi alimentare..."
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
                />
              </div>

              {/* Order */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Ordine Afișare
                </label>
                <input
                  type="number"
                  value={categoryForm.order}
                  onChange={(e) => handleCategoryFormChange('order', parseInt(e.target.value) || 0)}
                  min={0}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Categoriile vor fi afișate în ordinea crescătoare a acestui număr
                </p>
              </div>

              {/* Active Toggle */}
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div>
                  <label className="block text-sm font-medium text-gray-900">
                    Status Categorie
                  </label>
                  <p className="text-xs text-gray-500 mt-1">
                    Categoriile inactive nu vor fi vizibile pentru utilizatori
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => handleCategoryFormChange('isActive', !categoryForm.isActive)}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 ${
                    categoryForm.isActive ? 'bg-purple-600' : 'bg-gray-200'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      categoryForm.isActive ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-end gap-3">
              <button
                onClick={() => setShowCategoryModal(false)}
                disabled={savingCategory}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50"
              >
                Anulează
              </button>
              <button
                onClick={handleSaveCategory}
                disabled={savingCategory || !categoryForm.name.trim() || !categoryForm.slug.trim()}
                className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {savingCategory ? (
                  <>
                    <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Salvare...
                  </>
                ) : (
                  <>
                    {editingCategory ? '💾 Actualizează' : '✨ Creează Categorie'}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* FAQ Modal - Complete Professional Version */}
      {showFaqModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 overflow-y-auto">
          <div className="bg-white rounded-xl max-w-2xl w-full my-8">
            {/* Header */}
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-xl font-bold text-gray-900">
                {editingFaq ? '✏️ Editează FAQ' : '➕ FAQ Nou'}
              </h3>
              <button
                onClick={() => setShowFaqModal(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Form */}
            <div className="px-6 py-4 space-y-4 max-h-[calc(100vh-200px)] overflow-y-auto">
              {/* Question */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Întrebare <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={faqForm.question}
                  onChange={(e) => handleFaqFormChange('question', e.target.value)}
                  placeholder="ex: Care sunt beneficiile unei diete echilibrate?"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  autoFocus
                />
              </div>

              {/* Answer */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Răspuns <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={faqForm.answer}
                  onChange={(e) => handleFaqFormChange('answer', e.target.value)}
                  placeholder="Scrie un răspuns detaliat și util pentru utilizatori..."
                  rows={6}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
                />
              </div>

              {/* Category (optional) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Categorie (opțional)
                </label>
                <select
                  value={faqForm.categoryId}
                  onChange={(e) => handleFaqFormChange('categoryId', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                >
                  <option value="">Fără categorie</option>
                  {categories.map((cat) => (
                    <option key={cat.id} value={cat.id}>
                      {cat.icon} {cat.name}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  Poți asocia FAQ-ul cu o categorie specifică
                </p>
              </div>

              {/* Order */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Ordine Afișare
                </label>
                <input
                  type="number"
                  value={faqForm.order}
                  onChange={(e) => handleFaqFormChange('order', parseInt(e.target.value) || 0)}
                  min={0}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
                <p className="text-xs text-gray-500 mt-1">
                  FAQ-urile vor fi afișate în ordinea crescătoare a acestui număr
                </p>
              </div>

              {/* Published Toggle */}
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div>
                  <label className="block text-sm font-medium text-gray-900">
                    Status Publicare
                  </label>
                  <p className="text-xs text-gray-500 mt-1">
                    FAQ-urile nepublicate nu vor fi vizibile pentru utilizatori
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => handleFaqFormChange('isPublished', !faqForm.isPublished)}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 ${
                    faqForm.isPublished ? 'bg-purple-600' : 'bg-gray-200'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      faqForm.isPublished ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-end gap-3">
              <button
                onClick={() => setShowFaqModal(false)}
                disabled={savingFaq}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50"
              >
                Anulează
              </button>
              <button
                onClick={handleSaveFaq}
                disabled={savingFaq || !faqForm.question.trim() || !faqForm.answer.trim()}
                className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {savingFaq ? (
                  <>
                    <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Salvare...
                  </>
                ) : (
                  <>
                    {editingFaq ? '💾 Actualizează' : '✨ Creează FAQ'}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
